import { Component, Input, Output, EventEmitter, OnInit, OnChanges, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Socio } from '../../account/account.dto';
import { SocioService, UpdateSocioDto } from '../../services/socio.service';

@Component({
  selector: 'app-socio-birthdate-validation',
  templateUrl: './socio-birthdate-validation.component.html',
  styleUrls: ['./socio-birthdate-validation.component.scss']
})
export class SocioBirthdateValidationComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() socios: Socio[] = [];
  @Output() birthdateChange = new EventEmitter<Socio[]>();
  @Output() validationStatusChange = new EventEmitter<boolean>();

  form: FormGroup;
  errors: { [key: string]: string } = {};
  isFormInitialized = false;
  hasUserInteracted = false;

  constructor(
    private fb: FormBuilder,
    private socioService: SocioService
  ) {
    this.form = this.fb.group({});
  }

  ngOnInit() {
    // Ne pas initialiser ici, attendre que les socios soient disponibles
  }

  ngOnChanges() {
    if (this.socios && this.socios.length > 0) {
      this.isFormInitialized = false;
      this.hasUserInteracted = false; // Réinitialiser l'interaction utilisateur
      // Initialiser immédiatement
      this.initializeForm();
      this.isFormInitialized = true;
    } else {
      // Réinitialiser si pas de socios
      this.isFormInitialized = false;
      this.hasUserInteracted = false;
      this.form = this.fb.group({});
    }
  }

  ngAfterViewInit() {
    // S'assurer que le FormArray est initialisé après le rendu de la vue
    if (this.socios && this.socios.length > 0 && !this.isFormInitialized) {
      this.initializeForm();
      this.isFormInitialized = true;
    }
  }

  private initializeForm() {
    const formControls: { [key: string]: any } = {};

    this.socios.forEach((socio, index) => {
      // Convertir la date de naissance en objet Date si nécessaire
      let dateNaissance: Date | null = null;
      
      if (socio.date_naissance) {
        if (typeof socio.date_naissance === 'string') {
          // Si c'est une chaîne, la convertir en Date
          const dateString = socio.date_naissance as string;
          if (dateString.includes('-')) {
            const parts = dateString.split('-');
            if (parts.length === 3) {
              // Détecter le format : YYYY-MM-DD ou DD-MM-YYYY
              if (parts[0].length === 4) {
                // Format YYYY-MM-DD
                dateNaissance = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
              } else {
                // Format DD-MM-YYYY
                dateNaissance = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
              }
            }
          }
        } else if (socio.date_naissance instanceof Date) {
          dateNaissance = socio.date_naissance;
        }
      }
      
      // Vérifier que la date est valide
      if (dateNaissance && isNaN(dateNaissance.getTime())) {
        dateNaissance = null; // Laisser vide si la date est invalide
      }
      
      // Convertir la date en format string pour l'input HTML
      let dateValue = '';
      if (dateNaissance && !isNaN(dateNaissance.getTime())) {
        // Utiliser les méthodes locales pour éviter le décalage de fuseau horaire
        const year = dateNaissance.getFullYear();
        const month = String(dateNaissance.getMonth() + 1).padStart(2, '0');
        const day = String(dateNaissance.getDate()).padStart(2, '0');
        dateValue = `${year}-${month}-${day}`; // Format YYYY-MM-DD
      }
      
      formControls[`socio_${index}`] = this.fb.control(dateValue, Validators.required);
    });

    this.form = this.fb.group(formControls);
    
    // Émettre le statut de validation initial
    this.validationStatusChange.emit(this.isFormValid());
    
    // Écouter les changements du formulaire
    this.form.valueChanges.subscribe(value => {
      this.hasUserInteracted = true;
      this.validationStatusChange.emit(this.isFormValid());
    });
  }

  get isFormReady(): boolean {
    return this.isFormInitialized && this.socios && this.socios.length > 0;
  }

  getDateValue(socioIndex: number): string {
    const socio = this.socios[socioIndex];
    if (socio && socio.date_naissance) {
      let date: Date;
      
      if (typeof socio.date_naissance === 'string') {
        // Si c'est une chaîne, la convertir en Date
        const dateString = socio.date_naissance as string;
        if (dateString.includes('-')) {
          const parts = dateString.split('-');
          if (parts.length === 3) {
            // Détecter le format : YYYY-MM-DD ou DD-MM-YYYY
            if (parts[0].length === 4) {
              // Format YYYY-MM-DD
              date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
            } else {
              // Format DD-MM-YYYY
              date = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
            }
          } else {
            date = new Date(dateString);
          }
        } else {
          date = new Date(dateString);
        }
      } else if (socio.date_naissance instanceof Date) {
        date = socio.date_naissance;
      } else {
        return '';
      }
      
      // Vérifier que la date est valide
      if (date && !isNaN(date.getTime())) {
        // Utiliser les méthodes locales pour éviter le décalage de fuseau horaire
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    }
    return '';
  }

  onDateChange(socioIndex: number, newDateString: string) {
    const socio = this.socios[socioIndex];
    if (socio && newDateString) {
      // Marquer que l'utilisateur a interagi avec le formulaire
      this.hasUserInteracted = true;
      
      // Convertir la string en Date
      const dateObj = new Date(newDateString);
      
      // Vérifier que la date est valide
      if (isNaN(dateObj.getTime())) {
        console.error('Date invalide:', newDateString);
        return;
      }
      
      // Mettre à jour la date localement
      socio.date_naissance = dateObj;
      
      // Mettre à jour le FormControl aussi
      const control = this.form.get(`socio_${socioIndex}`);
      if (control) {
        control.setValue(newDateString);
      }
      
      // Émettre les changements pour que le parent puisse les gérer
      this.birthdateChange.emit(this.socios);
      
      // Émettre le statut de validation
      this.validationStatusChange.emit(this.isFormValid());
    }
  }

  // Méthode pour récupérer les socios avec leurs dates modifiées
  getUpdatedSocios(): Socio[] {
    if (!this.hasUserInteracted) {
      // Si l'utilisateur n'a pas interagi, ne retourner aucun socio
      return [];
    }
    
    // Retourner seulement les socios qui ont été modifiés
    return this.socios.filter((socio, index) => {
      const formValue = this.form.get(`socio_${index}`)?.value;
      return formValue && formValue !== this.getOriginalDateValue(socio, index);
    });
  }
  
  // Méthode pour récupérer la date originale
  private getOriginalDateValue(socio: Socio, index: number): string {
    // Retourner la date telle qu'elle était initialement
    if (socio.date_naissance) {
      if (typeof socio.date_naissance === 'string') {
        const dateString = socio.date_naissance as string;
        if (dateString.includes('-')) {
          const parts = dateString.split('-');
          if (parts.length === 3) {
            if (parts[0].length === 4) {
              // Format YYYY-MM-DD
              const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            } else {
              // Format DD-MM-YYYY
              const date = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            }
          }
        }
      } else if (socio.date_naissance instanceof Date) {
        const year = socio.date_naissance.getFullYear();
        const month = String(socio.date_naissance.getMonth() + 1).padStart(2, '0');
        const day = String(socio.date_naissance.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    }
    return '';
  }

  isFormValid(): boolean {
    // Permettre de passer à l'étape suivante si le formulaire est valide
    // même sans interaction utilisateur (si les dates sont déjà remplies)
    return this.form.valid;
  }

  getFormData(): Socio[] {
    const formValue = this.form.value;
    return this.socios.map((socio, index) => ({
      ...socio,
      date_naissance: formValue[`socio_${index}`]
    }));
  }

}
